import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { History, Search, Filter, Eye, Calendar, User as UserIcon, FileText } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Table, Badge, Button, Input, Select, DatePicker, Modal } from '../../components/common';
import { auditLogApi } from '../../services/api';
import './AuditLog.css';

export default function AuditLogList() {
    const [filters, setFilters] = useState({
        page: 1,
        limit: 50,
        action: '',
        entityType: '',
        dateFrom: '',
        dateTo: '',
        search: ''
    });
    const [selectedLog, setSelectedLog] = useState(null);

    const { data, isLoading } = useQuery({
        queryKey: ['audit-logs', filters],
        queryFn: () => auditLogApi.list(filters),
        select: (res) => res.data
    });

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleString('id-ID', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const getActionBadge = (action) => {
        const config = {
            'create': { variant: 'success', label: 'Create' },
            'update': { variant: 'info', label: 'Update' },
            'delete': { variant: 'danger', label: 'Delete' },
            'login': { variant: 'default', label: 'Login' },
            'logout': { variant: 'default', label: 'Logout' },
            'approve': { variant: 'success', label: 'Approve' },
            'reject': { variant: 'danger', label: 'Reject' },
            'submit': { variant: 'warning', label: 'Submit' }
        };
        const c = config[action?.toLowerCase()] || { variant: 'default', label: action };
        return <Badge variant={c.variant}>{c.label}</Badge>;
    };

    const columns = [
        {
            key: 'createdAt',
            title: 'Timestamp',
            width: 180,
            render: (date) => formatDate(date)
        },
        {
            key: 'user',
            title: 'User',
            render: (_, row) => (
                <div className="audit-user">
                    <UserIcon size={14} />
                    <span>{row.user?.fullName || 'System'}</span>
                </div>
            )
        },
        {
            key: 'action',
            title: 'Action',
            width: 100,
            render: (action) => getActionBadge(action)
        },
        {
            key: 'entityType',
            title: 'Entity',
            render: (type, row) => (
                <span className="audit-entity">
                    {type} {row.entityId ? `#${row.entityId.slice(0, 8)}...` : ''}
                </span>
            )
        },
        {
            key: 'description',
            title: 'Description',
            render: (desc) => <span className="audit-desc">{desc || '-'}</span>
        },
        {
            key: 'actions',
            title: '',
            width: 60,
            render: (_, row) => (
                <button className="audit-view-btn" onClick={() => setSelectedLog(row)}>
                    <Eye size={16} />
                </button>
            )
        }
    ];

    const actionOptions = [
        { value: '', label: 'All Actions' },
        { value: 'create', label: 'Create' },
        { value: 'update', label: 'Update' },
        { value: 'delete', label: 'Delete' },
        { value: 'login', label: 'Login' },
        { value: 'approve', label: 'Approve' },
        { value: 'reject', label: 'Reject' }
    ];

    const entityOptions = [
        { value: '', label: 'All Entities' },
        { value: 'User', label: 'User' },
        { value: 'PurchaseRequest', label: 'Purchase Request' },
        { value: 'PurchaseOrder', label: 'Purchase Order' },
        { value: 'Vendor', label: 'Vendor' },
        { value: 'Contract', label: 'Contract' },
        { value: 'Invoice', label: 'Invoice' }
    ];

    return (
        <div className="audit-log-page">
            <PageHeader
                title="Audit Log"
                subtitle="System activity history"
                breadcrumbs={[
                    { label: 'Settings', path: '/settings' },
                    { label: 'Audit Log' }
                ]}
            />

            <Card>
                <CardBody>
                    <div className="audit-filters">
                        <Input
                            placeholder="Search..."
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            icon={Search}
                        />
                        <Select
                            value={filters.action}
                            onChange={(e) => handleFilterChange('action', e.target.value)}
                            options={actionOptions}
                        />
                        <Select
                            value={filters.entityType}
                            onChange={(e) => handleFilterChange('entityType', e.target.value)}
                            options={entityOptions}
                        />
                        <DatePicker
                            placeholder="From"
                            value={filters.dateFrom}
                            onChange={(val) => handleFilterChange('dateFrom', val)}
                        />
                        <DatePicker
                            placeholder="To"
                            value={filters.dateTo}
                            onChange={(val) => handleFilterChange('dateTo', val)}
                        />
                    </div>
                </CardBody>
            </Card>

            <Card>
                <Table
                    columns={columns}
                    data={data?.data || []}
                    loading={isLoading}
                    rowKey="id"
                    pagination={data?.meta}
                    onPageChange={(page) => setFilters(prev => ({ ...prev, page }))}
                />
            </Card>

            {/* Detail Modal */}
            <Modal
                isOpen={!!selectedLog}
                onClose={() => setSelectedLog(null)}
                title="Audit Log Detail"
                size="md"
            >
                {selectedLog && (
                    <div className="audit-detail">
                        <div className="audit-detail-row">
                            <label>Timestamp</label>
                            <span>{formatDate(selectedLog.createdAt)}</span>
                        </div>
                        <div className="audit-detail-row">
                            <label>User</label>
                            <span>{selectedLog.user?.fullName || 'System'}</span>
                        </div>
                        <div className="audit-detail-row">
                            <label>Action</label>
                            <span>{getActionBadge(selectedLog.action)}</span>
                        </div>
                        <div className="audit-detail-row">
                            <label>Entity</label>
                            <span>{selectedLog.entityType}</span>
                        </div>
                        <div className="audit-detail-row">
                            <label>Entity ID</label>
                            <span className="mono">{selectedLog.entityId || '-'}</span>
                        </div>
                        <div className="audit-detail-row">
                            <label>Description</label>
                            <span>{selectedLog.description || '-'}</span>
                        </div>
                        {selectedLog.oldValues && (
                            <div className="audit-detail-row full">
                                <label>Old Values</label>
                                <pre>{JSON.stringify(selectedLog.oldValues, null, 2)}</pre>
                            </div>
                        )}
                        {selectedLog.newValues && (
                            <div className="audit-detail-row full">
                                <label>New Values</label>
                                <pre>{JSON.stringify(selectedLog.newValues, null, 2)}</pre>
                            </div>
                        )}
                        <div className="audit-detail-row">
                            <label>IP Address</label>
                            <span className="mono">{selectedLog.ipAddress || '-'}</span>
                        </div>
                        <div className="audit-detail-row">
                            <label>User Agent</label>
                            <span className="small">{selectedLog.userAgent || '-'}</span>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
